/**
 * Fachliche Auswertung für die Projektübersicht im Jour fixe: Problemsignale, was an einem
 * Antrag zur Genehmigung ansteht und was der Aufrufer sehen darf. Die Komponenten bekommen
 * fertige Werte.
 */
import { PermitProcedureSummary, ProjectSignals } from "../types";

export interface SignalBadge {
  /** Welches Symbol: Wecker für überfällige Termine, Ausrufezeichen für Probleme */
  kind: "overdue" | "problem";
  count: number;
  tooltip: string;
}

/** Die Problemsignale eines Projekts als Badges – überfällige Termine, dann Probleme; leer ohne Signal */
export const signalBadges = (signals?: ProjectSignals): SignalBadge[] => {
  if (!signals) return [];
  const badges: SignalBadge[] = [];
  if (signals.overdue > 0) {
    badges.push({
      kind: "overdue",
      count: signals.overdue,
      tooltip: `${signals.overdue} ${signals.overdue === 1 ? "überfälliger Termin" : "überfällige Termine"}`,
    });
  }
  if (signals.problems > 0) {
    badges.push({
      kind: "problem",
      count: signals.problems,
      tooltip: `${signals.problems} ${signals.problems === 1 ? "Problem" : "Probleme"} an Nachweisen oder Terminen`,
    });
  }
  return badges;
};

/** Wert des BImSchG-Status, solange das Genehmigungsverfahren läuft (`core/enums.py`) */
const BIMSCHG_LAEUFT = "Genehmigungsverfahren läuft";

/**
 * Ob die Genehmigung gerade läuft. Die aufgeklappte Zeile zeigt, was jetzt zu tun ist – und das
 * nur für die Statuskategorien, die laufen; vorerst gibt es dort nur die Genehmigung.
 */
export const permitsRunning = (project: { bimschg_status: string }): boolean =>
  project.bimschg_status === BIMSCHG_LAEUFT;

/** Ob über den Antrag entschieden ist – dann gibt es einen Bescheid */
export const isDecided = (procedure: PermitProcedureSummary): boolean =>
  !!procedure.bescheid_vom;

/**
 * Die Anträge in der Reihenfolge der aufgeklappten Zeile: erst die offenen, um die es gerade
 * geht, dann die entschiedenen – jeweils in der Reihenfolge des Servers (ältester zuerst).
 */
export const focusOrder = (
  procedures: PermitProcedureSummary[],
): PermitProcedureSummary[] => [
  ...procedures.filter((p) => !isDecided(p)),
  ...procedures.filter(isDecided),
];

/**
 * Überschrift eines Antrags: die Art in ihrem Kurznamen – sie sagt auf einen Blick, worum es
 * geht –, darunter die Bezeichnung, sofern sie etwas anderes sagt.
 */
export const procedureHeading = (
  procedure: PermitProcedureSummary,
): { title: string; subtitle: string | null } => {
  const title = procedure.procedure_type_short || procedure.procedure_type;
  return {
    title,
    subtitle: procedure.label !== title ? procedure.label : null,
  };
};

/**
 * Das Datum, an dem die Genehmigung hängt: der Bescheid, sobald es ihn gibt, sonst die geplante
 * Genehmigung. Nur die geplante lässt sich ändern.
 */
export const approvalDate = (
  procedure: PermitProcedureSummary,
): { date: string | null; label: string; plannable: boolean } =>
  procedure.bescheid_vom
    ? { date: procedure.bescheid_vom, label: "Bescheid vom", plannable: false }
    : {
        date: procedure.genehmigung_geplant_am,
        label: "Geplante Genehmigung",
        plannable: true,
      };

/**
 * Ob man alle Termine eines Projekts sieht: Termine an Antrag und Nachweis gehören zur
 * Genehmigung und erscheinen nur mit Lesezugriff auf beide Bereiche.
 */
export const seesAllProjectDeadlines = (
  rights: Record<string, string> | undefined,
): boolean =>
  !!rights?.["genehmigung.antraege"] && !!rights?.["genehmigung.nachweise"];

/** Ob man die Anträge eines Projekts sehen darf */
export const seesPermitProcedures = (
  rights: Record<string, string> | undefined,
): boolean => !!rights?.["genehmigung.antraege"];

/** Ob man an den Anträgen eines Projekts etwas ändern darf – geplante Genehmigung, Bearbeitung */
export const editsPermitProcedures = (
  rights: Record<string, string> | undefined,
): boolean => rights?.["genehmigung.antraege"] === "schreiben";
